import ExcelJS from 'exceljs';

function sumChildrenAmounts(node) {
  let monthTotals = {};
  let yearTotals = {};

  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      const { monthTotals: childMonthTotals, yearTotals: childYearTotals } = sumChildrenAmounts(child);
      for (const locationId in childMonthTotals) {
        if (!monthTotals[locationId]) monthTotals[locationId] = 0;
        monthTotals[locationId] += childMonthTotals[locationId];
      }
      for (const locationId in childYearTotals) {
        if (!yearTotals[locationId]) yearTotals[locationId] = 0;
        yearTotals[locationId] += childYearTotals[locationId];
      }
    });
  } else {
    monthTotals = node.monthTotals || {};
    yearTotals = node.yearTotals || {};
  }

  node.monthTotals = monthTotals;
  node.yearTotals = yearTotals;

  return { monthTotals, yearTotals };
}

function flattenTree(data, level = 0, result = [], parentNature = 1) {
  data.forEach(node => {
    const nodeNature = node.nature || parentNature;
    const alwaysInclude = [4, 5, 6, 7, 8, 449, 451].includes(node.id);
    const hasNonZeroTotals = Object.values(node.monthTotals).some(total => total !== 0) ||
                             Object.values(node.yearTotals).some(total => total !== 0);
  
    if (alwaysInclude || hasNonZeroTotals) {
      const totalCurrentMonth = Object.values(node.monthTotals).reduce((sum, value) => sum + value, 0);
      const totalCurrentYear = Object.values(node.yearTotals).reduce((sum, value) => sum + value, 0);
      let subcategoryPercentages = {};
  
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          const childTotalCurrentMonth = Object.values(child.monthTotals).reduce((sum, value) => sum + value, 0);
          const childTotalCurrentYear = Object.values(child.yearTotals).reduce((sum, value) => sum + value, 0);
  
          const percentageCurrentMonth = childTotalCurrentMonth / totalCurrentMonth * 100;
          const percentageCurrentYear = childTotalCurrentYear / totalCurrentYear * 100;
  
          subcategoryPercentages[child.name] = {
            monthPercentage: isNaN(percentageCurrentMonth) ? 0 : percentageCurrentMonth,
            yearPercentage: isNaN(percentageCurrentYear) ? 0 : percentageCurrentYear
          };
        });
      }
  
      result.push({
        id: node.id,
        name: node.name,
        parent: node.parent,
        nature: nodeNature,
        code: node.code,
        note: node.note,
        account_type_id: node.account_type_id,
        dgii_category_id: node.dgii_category_id,
        status: node.status,
        level: level,
        monthTotals: node.monthTotals,
        yearTotals: node.yearTotals,
        isParent: alwaysInclude,
        subcategoryPercentages: subcategoryPercentages
      });
  
      if (node.children && node.children.length > 0) {
        flattenTree(node.children, level + 1, result, nodeNature);
      }
    }
  });
  return result;
}

export const generateEstadoResultadoExcel = async (all_accounts, processed_accounts, info, all_locations) => {
  const addAmountsToAccounts = (accounts, processed) => {
    accounts.forEach(account => {
      let monthTotals = {};
      let yearTotals = {};

      processed.forEach(procAccount => {
        if (procAccount.chart_of_account_id === account.id) {
          const locationId = procAccount.location_id;
          if (!monthTotals[locationId]) monthTotals[locationId] = 0;
          if (!yearTotals[locationId]) yearTotals[locationId] = 0;

          const monthTotal = parseFloat(procAccount.month_total) || 0;
          const yearTotal = parseFloat(procAccount.year_total) || 0;

          if (procAccount.chart_of_account_type_id === 4 && ![449, 451].includes(account.id)) {
            monthTotals[locationId] += monthTotal;
            yearTotals[locationId] += yearTotal;
          } else if ((procAccount.chart_of_account_type_id === 5 || procAccount.chart_of_account_type_id === 6) && ![449, 451].includes(account.id)) {
            monthTotals[locationId] += monthTotal;
            yearTotals[locationId] += yearTotal;
          }
        }
      });

      account.monthTotals = monthTotals;
      account.yearTotals = yearTotals;

      // Si tiene hijos, añadir cantidades a los hijos
      if (account.children && account.children.length > 0) {
        addAmountsToAccounts(account.children, processed);
      }

      // Añadir nodos de subcategorías directamente bajo la cuenta principal
      let subcategoryMap = {};

      processed.forEach(procAccount => {
        if (procAccount.chart_of_account_id === account.id && procAccount.subcategory_id) {
          const locationId = procAccount.location_id;

          // Crear o actualizar la subcategoría
          if (!subcategoryMap[procAccount.subcategory_id]) {
            subcategoryMap[procAccount.subcategory_id] = {
              id: procAccount.subcategory_id,
              name: procAccount.subcategory_name,
              parent: account.id,
              children: [],
              monthTotals: {},
              yearTotals: {},
              level: account.level + 1
            };
          }

          let subcategoryNode = subcategoryMap[procAccount.subcategory_id];

          // Actualizar los montos en la subcategoría
          subcategoryNode.monthTotals[locationId] = (subcategoryNode.monthTotals[locationId] || 0) + (procAccount.chart_of_account_type_id === 5 || procAccount.chart_of_account_type_id === 6 ? parseFloat(procAccount.month_total) : parseFloat(procAccount.month_total)) || 0;
          subcategoryNode.yearTotals[locationId] = (subcategoryNode.yearTotals[locationId] || 0) + (procAccount.chart_of_account_type_id === 5 || procAccount.chart_of_account_type_id === 6 ? parseFloat(procAccount.year_total) : parseFloat(procAccount.year_total)) || 0;
        }
      });

      // Añadir las subcategorías al árbol de cuentas principal
      for (let subcategoryId in subcategoryMap) {
        account.children.push(subcategoryMap[subcategoryId]);
      }

      // Añadir montos a la cuenta principal si no tiene subcategoría
      if (!account.children.length) {
        processed.forEach(procAccount => {
          if (procAccount.chart_of_account_id === account.id) {
            const locationId = procAccount.location_id;
            const monthTotal = parseFloat(procAccount.month_total) || 0;
            const yearTotal = parseFloat(procAccount.year_total) || 0;

            monthTotals[locationId] += monthTotal;
            yearTotals[locationId] += yearTotal;
          }
        });
        account.monthTotals = monthTotals;
        account.yearTotals = yearTotals;
      }
    });
  };

  addAmountsToAccounts(all_accounts, processed_accounts);

  all_accounts.forEach(node => sumChildrenAmounts(node));
  const otrosEgresosIndex = all_accounts.findIndex(acc => acc.id === 451);

  if (otrosEgresosIndex !== -1) {
    // Insertar una fila vacía después de 'Otros Egresos'
    all_accounts.splice(otrosEgresosIndex + 1, 0,  {
      id: '',
      name: '',
      monthTotals: {},
      yearTotals: {},
      level: 0,
      isParent: false,
      children: []
    });
  }
  // Definir las cuentas principales
  let ingresos = all_accounts.find(acc => acc.id === 5);
  let costos = all_accounts.find(acc => acc.id === 6);
  let gastos = all_accounts.find(acc => acc.id === 7);
  let otrosIngresos = all_accounts.find(acc => acc.id === 449);
  let otrosEgresos = all_accounts.find(acc => acc.id === 451);
  
  if (!ingresos || !costos || !gastos || !otrosIngresos || !otrosEgresos) {
    console.error("Ingresos, Costos, Gastos, Otros Ingresos, or Otros Egresos account not found.");
    return [];
  }

  ingresos.nature = -1;
  costos.nature = 1;
  gastos.nature = 1;
  otrosIngresos.nature = -1;
  otrosEgresos.nature = 1;

  // Añadir contribución marginal y utilidad operativa
  let contribucionMarginal = {
    id: 'contribucionMarginal',
    name: 'Contribución Marginal',
    monthTotals: {},
    yearTotals: {},
    nature: 1,
    level: costos.level + 1,
    isParent: false,
    children: []
  };

  let utilidadOperaciones = {
    id: 'utilidadOperaciones',
    name: 'Utilidad Operativa',
    monthTotals: {},
    yearTotals: {},
    nature: 1,
    level: gastos.level + 1,
    isParent: false,
    children: []
  };

  let resultadoNeto = {
    id: 'resultadoNeto',
    name: 'Resultado Neto',
    monthTotals: {},
    yearTotals: {},
    nature: 1,
    level: 0,
    isParent: false,
    children: []
  };

  const locationIds = new Set([...Object.keys(ingresos.monthTotals), ...Object.keys(costos.monthTotals), ...Object.keys(gastos.monthTotals), ...Object.keys(otrosIngresos.monthTotals), ...Object.keys(otrosEgresos.monthTotals)]);

  locationIds.forEach(locationId => {
    const ingresosMonthTotal = ingresos.monthTotals[locationId] || 0;
    const costosMonthTotal = costos.monthTotals[locationId] || 0;
    const gastosMonthTotal = gastos.monthTotals[locationId] || 0;
    const otrosIngresosMonthTotal = otrosIngresos.monthTotals[locationId] || 0;
    const otrosEgresosMonthTotal = otrosEgresos.monthTotals[locationId] || 0;

    const ingresosYearTotal = ingresos.yearTotals[locationId] || 0;
    const costosYearTotal = costos.yearTotals[locationId] || 0;
    const gastosYearTotal = gastos.yearTotals[locationId] || 0;
    const otrosIngresosYearTotal = otrosIngresos.yearTotals[locationId] || 0;
    const otrosEgresosYearTotal = otrosEgresos.yearTotals[locationId] || 0;

    contribucionMarginal.monthTotals[locationId] = -ingresosMonthTotal - costosMonthTotal;
    contribucionMarginal.yearTotals[locationId] = -ingresosYearTotal - costosYearTotal;

    utilidadOperaciones.monthTotals[locationId] = contribucionMarginal.monthTotals[locationId] - gastosMonthTotal;
    utilidadOperaciones.yearTotals[locationId] = contribucionMarginal.yearTotals[locationId] - gastosYearTotal;

    resultadoNeto.monthTotals[locationId] = utilidadOperaciones.monthTotals[locationId] - otrosIngresosMonthTotal - otrosEgresosMonthTotal;
    resultadoNeto.yearTotals[locationId] = utilidadOperaciones.yearTotals[locationId] - otrosIngresosYearTotal - otrosEgresosYearTotal;
  });

  const costosIndex = all_accounts.indexOf(costos);
  const gastosIndex = all_accounts.indexOf(gastos);

  if (costosIndex !== -1) {
    all_accounts.splice(costosIndex + 1, 0, contribucionMarginal);
  }

  if (gastosIndex !== -1) {
    all_accounts.splice(gastosIndex + 2, 0, utilidadOperaciones);
  }

  all_accounts.push(resultadoNeto);

  const flattenedData = flattenTree(all_accounts);
  const locationIdArray = Array.from(locationIds);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Estado de Resultados');

  const headerStyle = {
    font: { name: 'Arial', size: 14, bold: true },
    alignment: { vertical: 'middle', horizontal: 'center' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D3D3D3' } }
  };

  worksheet.addRow([]);
  worksheet.addRow([]);
  worksheet.addRow([]);
  worksheet.addRow([]);

  worksheet.mergeCells('A1:J1');
  worksheet.mergeCells('A2:J2');
  worksheet.mergeCells('A3:J3');
  worksheet.mergeCells('A4:J4');

  worksheet.getCell('A1').value = info.business_name;
  worksheet.getCell('A2').value = 'Estado de Resultados';
  worksheet.getCell('A3').value = `Valor expresado en (${info.currency_code})`;
  worksheet.getCell('A4').value = `Para el Período ${info.date_from} al ${info.date_to}`;

  worksheet.getCell('A1').font = headerStyle.font;
  worksheet.getCell('A1').alignment = headerStyle.alignment;
  worksheet.getCell('A1').fill = headerStyle.fill;

  worksheet.getCell('A2').font = headerStyle.font;
  worksheet.getCell('A2').alignment = headerStyle.alignment;
  worksheet.getCell('A2').fill = headerStyle.fill;

  worksheet.getCell('A3').font = headerStyle.font;
  worksheet.getCell('A3').alignment = headerStyle.alignment;
  worksheet.getCell('A3').fill = headerStyle.fill;

  worksheet.getCell('A4').font = headerStyle.font;
  worksheet.getCell('A4').alignment = headerStyle.alignment;
  worksheet.getCell('A4').fill = headerStyle.fill;

  worksheet.getRow(1).height = 20;
  worksheet.getRow(2).height = 20;
  worksheet.getRow(3).height = 20;
  worksheet.getRow(4).height = 20;

  const headerRow = worksheet.addRow(['Código cuenta', 'Nombre cuenta', ...locationIdArray.map(id => {
    const location = all_locations.find(l => l.id == id);
    return location ? location.desc_loc_long : `Ubicación ${id}`;
  }), 'Monto Actual', ...locationIdArray.map(id => {
    const location = all_locations.find(l => l.id ==id);
    return location ? `Monto acumulado ${location.desc_loc_long}` : `Monto acumulado ${id}`;
  }), 'Monto Acumulado']);
  headerRow.font = { bold: true };

  worksheet.columns = [
    { header: 'Código cuenta', key: 'code', width: 15 },
    { header: 'Nombre cuenta', key: 'name', width: 50 },
    ...locationIdArray.map(id => {
      const location = all_locations.find(l => l.id ==id);
      return { header: location ? location.desc_loc_long : `Ubicación ${id}`, key: `month_${id}`, width: 30 };
    }),
    { header: 'Monto Actual', key: 'total_month', width: 30 },
    ...locationIdArray.map(id => {
      const location = all_locations.find(l => l.id == id);
      return { header: location ? `Monto acumulado ${location.desc_loc_long}` : `Monto acumulado ${id}`, key: `year_${id}`, width: 30 };
    }),
    { header: 'Monto Acumulado', key: 'total_year', width: 30 }
  ];

  // Definir las referencias para las filas
  const rowReferences = { month: [], year: [] };
  locationIdArray.forEach(id => {
    rowReferences[`month_${id}`] = [];
    rowReferences[`year_${id}`] = [];
  });

  flattenedData.forEach((row, index) => {
    const indent = ' '.repeat(row.level * 4);
    const newRowData = {
      code: row.code,
      name: indent + row.name,
      total_month: Object.values(row.monthTotals).reduce((sum, value) => sum + value, 0) * (row.nature || 1),
      total_year: Object.values(row.yearTotals).reduce((sum, value) => sum + value, 0) * (row.nature || 1)
    };
    locationIdArray.forEach(id => {
      newRowData[`month_${id}`] = (row.monthTotals[id] || 0) * (row.nature || 1);
      newRowData[`year_${id}`] = (row.yearTotals[id] || 0) * (row.nature || 1);
    });

    const newRow = worksheet.addRow(newRowData);

    // Guardar referencias de fila para los resultados netos
    if (row.isParent || row.id === 'contribucionMarginal' || row.id === 'utilidadOperaciones' || row.id === 'resultadoNeto') {
      rowReferences.month.push(newRow.getCell('total_month').address);
      rowReferences.year.push(newRow.getCell('total_year').address);
      locationIdArray.forEach(id => {
        rowReferences[`month_${id}`].push(newRow.getCell(`month_${id}`).address);
        rowReferences[`year_${id}`].push(newRow.getCell(`year_${id}`).address);
      });
      newRow.font = { bold: true, size: 16 };
      locationIdArray.forEach((id, index) => {
        newRow.getCell(3 + index).alignment = { horizontal: 'right' };
        newRow.getCell(3 + locationIdArray.length + index + 1).alignment = { horizontal: 'right' };
      });
    } else {
      // Poner en negrita las subcategorías
      if (row.level === 1) { // Asumiendo que las subcategorías están en el nivel 1
        newRow.font = { bold: true };
      }
      locationIdArray.forEach((id, index) => {
        newRow.getCell(3 + index).alignment = { horizontal: 'right' };
        newRow.getCell(3 + locationIdArray.length + index + 1).alignment = { horizontal: 'right' };
      });
    }
  });

  worksheet.addRow(['', '', '', '', '', '', '', '', '', '']);
  worksheet.addRow(['', '', '', '', '', '', '', '', '', '']);

  // Agregar fila de resultados netos



  const accountingFormat = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';

  worksheet.getCell('A1').value = info.business_name;
  worksheet.getCell('A2').value = 'Estado de Resultados';
  worksheet.getCell('A3').value = `Valor expresado en (${info.currency_code})`;
  worksheet.getCell('A4').value = `Para el Período ${info.date_from} al ${info.date_to}`;

  worksheet.eachRow({ includeEmpty: true }, row => {
    row.eachCell({ includeEmpty: true }, cell => {
      cell.numFmt = accountingFormat;
    });
  });

  flattenedData.forEach((row, index) => {
    const currentRow = worksheet.getRow(index + 6);
    currentRow.outlineLevel = row.level;
  });

  worksheet.properties.outlineProperties = {
    summaryBelow: false,
    summaryRight: false,
  };

  locationIdArray.forEach((id, index) => {
    const colIndex = 3 + index;
    worksheet.getColumn(colIndex).outlineLevel = 1;
    worksheet.getColumn(colIndex + locationIdArray.length + 1).outlineLevel = 1;
  });

  flattenedData.forEach((row, index) => {
    const currentRow = worksheet.getRow(index + 6);
    if (!row.isParent) {
      let hasValue = false;
      locationIdArray.forEach((id, locIndex) => {
        if (currentRow.getCell(3 + locIndex).value) {
          currentRow.getCell(3 + locIndex).border = {
            bottom: { style: 'thin' }
          };
          hasValue = true;
        }
        if (currentRow.getCell(3 + locationIdArray.length + locIndex + 1).value) {
          currentRow.getCell(3 + locationIdArray.length + locIndex + 1).border = {
            bottom: { style: 'thin' }
          };
          hasValue = true;
        }
      });
      if (currentRow.getCell('total_month').value) {
        currentRow.getCell('total_month').border = {
          bottom: { style: 'thin' }
        };
        hasValue = true;
      }
      if (currentRow.getCell('total_year').value) {
        currentRow.getCell('total_year').border = {
          bottom: { style: 'thin' }
        };
        hasValue = true;
      }

      if (hasValue) {
        if (index + 1 < flattenedData.length && flattenedData[index + 1].isParent) {
          locationIdArray.forEach((id, locIndex) => {
            if (currentRow.getCell(3 + locIndex).value) {
              currentRow.getCell(3 + locIndex).border = {
                bottom: { style: 'double' }
              };
            }
            if (currentRow.getCell(3 + locationIdArray.length + locIndex + 1).value) {
              currentRow.getCell(3 + locationIdArray.length + locIndex + 1).border = {
                bottom: { style: 'double' }
              };
            }
          });
          if (currentRow.getCell('total_month').value) {
            currentRow.getCell('total_month').border = {
              bottom: { style: 'double' }
            };
          }
          if (currentRow.getCell('total_year').value) {
            currentRow.getCell('total_year').border = {
              bottom: { style: 'double' }
            };
          }
        }
      }
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const nameFile = `Estado de Resultados - ${info.business_name} - Para el Período ${info.date_from} al ${info.date_to}`;
  link.download = `${nameFile}.xlsx`;
  link.click();
};
